import { action, computed, makeObservable, observable, runInAction } from 'mobx';
import { getRootStoreInstance } from 'store/rootStore';
import { DynamicFormDelegate } from '@insurancemenu/imenu-components-lib/dist/utils/forms/dynamic-forms/DynamicFormDelegate';
import { FormObjectFromJSON } from '@insurancemenu/imenu-components-lib/dist/types';
import { FormInstanceType, FormStatuses } from 'types/formService';
import { USER_ACTIONS } from 'types/enrollmentService';
import { requestApplyUserAction } from 'api/operations/enrollmentService';
import { requestContributionFormInstance, requestSetAnswers } from 'api/operations/formService';
import { EnrollmentFlowDomainParams } from 'store/flows/enrollment/enrollmentFlowPage';
import { createContext, useContext } from 'react';
import { EnrollmentHeaderSafeguardDelegate } from 'store/flows/enrollment/enrollmentHeaderSafeguardDelegate';
import { bpHistory } from 'global/bpHistory';
import EnrollmentPeriodModule from 'store/modules/enrollment/enrollmentPeriodModule';

export default class ContributionFormPageStore {
  private _rootStore = getRootStoreInstance();
  private _domainData: EnrollmentFlowDomainParams;
  private _formInstance: null | FormInstanceType = null;
  private _dynamicFormDelegate: null | DynamicFormDelegate = null;
  private _enrollmentPeriodModule = new EnrollmentPeriodModule();
  public safeguardDelegate = new EnrollmentHeaderSafeguardDelegate({
    onSaveChanges: this.setAnswers.bind(this),
    onNoSavingExit: this.onBackRedirect.bind(this),
    isStepCompleted: false,
    enrollmentPeriodModule: this._enrollmentPeriodModule,
  });

  private _createFormDelegate(formObject: FormObjectFromJSON): void {
    this._dynamicFormDelegate = new DynamicFormDelegate({
      currentUserRoles: this._rootStore.userStore.userRoles,
      fetchForm: async (): Promise<FormObjectFromJSON> => {
        return new Promise((resolve) => {
          resolve(formObject);
        });
      },
      onSubmit: this.submitForm.bind(this),
    });
  }

  private _isInitialDataLoading = false;

  public get isInitialDataLoading(): boolean {
    return this._isInitialDataLoading;
  }

  get enrollmentPeriodModule(): EnrollmentPeriodModule {
    return this._enrollmentPeriodModule;
  }

  public get dynamicFormDelegate(): DynamicFormDelegate | null {
    return this._dynamicFormDelegate;
  }

  public setIsInitialDataLoading(isInitialDataLoading: boolean): void {
    this._isInitialDataLoading = isInitialDataLoading;
  }

  private async _getFormInstance(): Promise<void> {
    const formInstance = await requestContributionFormInstance(this._domainData.periodId);

    runInAction(() => {
      this._createFormDelegate(formInstance!.form);
      this._formInstance = formInstance;
      void this._dynamicFormDelegate!.buildForm(
        formInstance!.id,
        formInstance!.answers.common as Record<string, string>,
      );
      this.safeguardDelegate.setIsStepCompleted(formInstance!.status === FormStatuses.COMPLETE);
    });
  }

  async getInitialData(): Promise<void> {
    this.setIsInitialDataLoading(true);
    await Promise.all([
      this._enrollmentPeriodModule.uploadEnrollmentPeriod(this._domainData.periodId),
      this._getFormInstance(),
    ]);
    runInAction(() => {
      this.setIsInitialDataLoading(false);
    });
  }

  public onBackRedirect(): void {
    bpHistory.push(`/checklist/${this._domainData.periodId}`);
  }

  async setAnswers(): Promise<void> {
    if (this._formInstance && this._dynamicFormDelegate) {
      this._dynamicFormDelegate.formController?.setLoading(true);
      await requestSetAnswers({
        formInstanceId: this._formInstance.id,
        answers: { common: this._dynamicFormDelegate.formAnswers, custom: {} },
        complete: false,
      });

      runInAction(() => {
        this._dynamicFormDelegate!.formController?.setLoading(false);
        this.onBackRedirect();
      });
    }
  }

  async submitForm(): Promise<void> {
    if (this._formInstance && this._dynamicFormDelegate) {
      await requestSetAnswers({
        formInstanceId: this._formInstance.id,
        answers: { common: this._dynamicFormDelegate.formAnswers, custom: {} },
        complete: true,
      });
      await requestApplyUserAction(this._domainData.periodId, USER_ACTIONS.COMPLETE_CONTRIBUTION);
      runInAction(() => {
        this.onBackRedirect();
      });
    }
  }

  constructor(domainParams: EnrollmentFlowDomainParams) {
    this._domainData = domainParams;

    makeObservable<
      ContributionFormPageStore,
      '_dynamicFormDelegate' | '_isInitialDataLoading' | '_formInstance' | '_getFormInstance'
    >(this, {
      _dynamicFormDelegate: observable,
      _isInitialDataLoading: observable,
      _formInstance: observable,
      safeguardDelegate: observable,
      setIsInitialDataLoading: action.bound,
      _getFormInstance: action.bound,
      getInitialData: action.bound,
      onBackRedirect: action.bound,
      setAnswers: action.bound,
      submitForm: action.bound,
      enrollmentPeriodModule: computed,
      dynamicFormDelegate: computed,
    });
  }
}

export const ContributionFormPageStoreContext = createContext(
  new ContributionFormPageStore({} as EnrollmentFlowDomainParams),
);

export const useContributionFormPageStore = (): ContributionFormPageStore => {
  return useContext<ContributionFormPageStore>(ContributionFormPageStoreContext);
};
